import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useI18n } from '../../i18n/I18nContext'
import type { MfaEnrollmentDetails } from '../../services/auth'
import { QrCode } from '../../utils/qrcode'
import { QrCodeImage } from './QrCodeImage'

interface MfaEnrollmentProps {
  /** Pending enrollment material, or null before enrollment has started. */
  enrollment: MfaEnrollmentDetails | null
  loading: boolean
  error: string
  verificationCode: string
  onVerificationCodeChange: (value: string) => void
  /** Submit handler for confirming the six-digit code. */
  onConfirm: (event: React.FormEvent) => void
  /** Begin (or restart) a fresh enrollment. */
  onStart: () => void
  /** Abandon the current enrollment; the caller clears the secret from memory. */
  onCancel: () => void
  /** The pending enrollment window elapsed; the caller clears the secret. */
  onExpire: () => void
  /** True when the previous enrollment window elapsed (shown on the start view). */
  expired?: boolean
}

const QR_SIZE = 220

/**
 * Owner MFA enrollment screen.
 *
 * The primary path is a locally generated QR code encoding the backend-provided
 * `otpauth://` URI — nothing is uploaded to a QR service. Manual setup is a
 * secondary, collapsed fallback that exposes the setup key only during pending
 * enrollment, masked until explicitly revealed. The raw URI is never rendered
 * as visible text, written to a DOM data attribute, or persisted to storage.
 */
export const MfaEnrollment: React.FC<MfaEnrollmentProps> = ({
  enrollment,
  loading,
  error,
  verificationCode,
  onVerificationCodeChange,
  onConfirm,
  onStart,
  onCancel,
  onExpire,
  expired = false,
}) => {
  const { t } = useI18n()
  const [manualOpen, setManualOpen] = useState(false)
  const [revealed, setRevealed] = useState(false)
  const [copied, setCopied] = useState(false)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Move focus to the heading when enrollment begins so keyboard/screen-reader
  // users land at the top of the setup flow.
  useEffect(() => {
    if (enrollment) headingRef.current?.focus()
  }, [Boolean(enrollment)])

  // Reset transient UI state whenever the enrollment identity changes (new
  // enrollment) or is cleared (success/cancel). This keeps a revealed secret
  // from surviving across enrollments.
  useEffect(() => {
    setManualOpen(false)
    setRevealed(false)
    setCopied(false)
  }, [enrollment?.otpauthUri])

  // Enforce the enrollment expiration window client-side. When it elapses we
  // ask the caller to drop the secret from memory and show a safe notice.
  useEffect(() => {
    if (!enrollment) return
    const ms = Math.max(0, enrollment.expiresInSeconds * 1000)
    const timer = setTimeout(() => {
      onExpire()
    }, ms)
    return () => clearTimeout(timer)
  }, [enrollment?.otpauthUri])

  // Clear any pending "Copied" timer on unmount.
  useEffect(() => () => {
    if (copyTimer.current) clearTimeout(copyTimer.current)
  }, [])

  // Precompute whether the URI can be encoded locally so we can fall back to the
  // manual key without leaking the URI if QR generation fails.
  const qrFailed = useMemo(() => {
    if (!enrollment) return false
    try {
      QrCode.encodeText(enrollment.otpauthUri)
      return false
    } catch {
      return true
    }
  }, [enrollment?.otpauthUri])

  const maskedSecret = useMemo(() => {
    if (!enrollment) return ''
    return enrollment.secret.replace(/./g, '•')
  }, [enrollment?.secret])

  if (!enrollment) {
    return (
      <div className="stack" data-testid="mfa-enrollment-start">
        <h2>{t('Set up your authenticator')}</h2>
        <p>{t('Start enrollment to protect your account with an authenticator app.')}</p>
        {expired && (
          <p className="error-text" role="status">{t('Your enrollment session expired. Start again to get a new setup key.')}</p>
        )}
        {error && <p className="error-text" role="alert">{t(error)}</p>}
        <button type="button" className="btn btn-primary" disabled={loading} onClick={onStart}>
          {t('Start MFA enrollment')}
        </button>
      </div>
    )
  }

  const handleReveal = () => setRevealed((prev) => !prev)

  const handleCopy = async () => {
    // Copy the setup key only — never the full otpauth URI.
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(enrollment.secret)
        setCopied(true)
        if (copyTimer.current) clearTimeout(copyTimer.current)
        copyTimer.current = setTimeout(() => setCopied(false), 2000)
      }
    } catch {
      // Clipboard can fail (permissions/insecure context). Surface the key so
      // the user can copy manually rather than leaving them stuck.
      setRevealed(true)
      setCopied(false)
    }
  }

  return (
    <form onSubmit={onConfirm} className="stack" data-testid="mfa-enrollment-form">
      <h2 ref={headingRef} tabIndex={-1}>{t('Set up your authenticator')}</h2>
      <p id="mfa-enrollment-instructions">
        {t('Scan this QR code with an authenticator app such as Google Authenticator, Microsoft Authenticator, 1Password, or Authy, then enter the six-digit code it shows.')}
      </p>

      <div className="mfa-qr" aria-describedby="mfa-enrollment-instructions">
        {qrFailed ? (
          <p className="error-text" role="alert" data-testid="mfa-qr-error">
            {t('The QR code could not be displayed. Use the manual setup key below.')}
          </p>
        ) : (
          <QrCodeImage
            value={enrollment.otpauthUri}
            size={QR_SIZE}
            label={t('QR code for setting up time-based multifactor authentication.')}
          />
        )}
      </div>

      <p className="helper" data-testid="mfa-enrollment-account">{enrollment.issuer} — {enrollment.accountName}</p>

      <details
        className="mfa-manual"
        open={manualOpen || qrFailed}
        onToggle={(event) => setManualOpen((event.target as HTMLDetailsElement).open)}
      >
        <summary data-testid="mfa-manual-toggle">{t('Can’t scan the QR code? Enter a setup key manually.')}</summary>
        <dl className="mfa-manual-details">
          <div className="mfa-manual-row">
            <dt>{t('Account')}</dt>
            <dd>{enrollment.accountName}</dd>
          </div>
          <div className="mfa-manual-row">
            <dt>{t('Issuer')}</dt>
            <dd>{enrollment.issuer}</dd>
          </div>
          <div className="mfa-manual-row">
            <dt>{t('Setup key')}</dt>
            <dd>
              <code data-testid="mfa-manual-secret" aria-label={revealed ? undefined : t('Setup key hidden')}>
                {revealed ? enrollment.secret : maskedSecret}
              </code>
              <span className="mfa-manual-actions">
                <button type="button" className="btn btn-secondary btn-sm" onClick={handleReveal} data-testid="mfa-reveal-secret">
                  {revealed ? t('Hide') : t('Reveal')}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={handleCopy} data-testid="mfa-copy-secret">
                  {copied ? t('Copied') : t('Copy key')}
                </button>
              </span>
            </dd>
          </div>
          <div className="mfa-manual-row">
            <dt>{t('Type')}</dt>
            <dd>{t('Time based')}</dd>
          </div>
          <div className="mfa-manual-row">
            <dt>{t('Digits')}</dt>
            <dd>6</dd>
          </div>
          <div className="mfa-manual-row">
            <dt>{t('Period')}</dt>
            <dd>{t('30 seconds')}</dd>
          </div>
        </dl>
        <p className="sr-only" role="status" aria-live="polite">
          {copied ? t('Setup key copied.') : ''}
        </p>
      </details>

      <div className="form-row">
        <label htmlFor="mfaEnrollmentCode">{t('Six-digit verification code')}</label>
        <input
          id="mfaEnrollmentCode"
          className="input"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={6}
          value={verificationCode}
          onChange={(event) => onVerificationCodeChange(event.target.value)}
          required
        />
      </div>

      {error && <p className="error-text" role="alert">{t(error)}</p>}

      <div className="mfa-enrollment-controls">
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? t('Verifying...') : t('Enable MFA')}
        </button>
        <button type="button" className="btn btn-secondary" disabled={loading} onClick={onCancel} data-testid="mfa-cancel">
          {t('Cancel')}
        </button>
      </div>
    </form>
  )
}
